import { basePath } from "@Common/constants";
import type {
	AnalyticsPayloadSchema,
	BrowserInfo,
} from "@Common/types/bo/analytics_public/analytics_public";
import type {
	FullPayloadSchema,
	GeolocationCoordinates,
} from "@Common/types/bo/applications_public/applications_public";
import type { ComponentPropI } from "@Common/types/resources";
import type {
	OnFileUploadPayload,
	OnKeystrokePayload,
	OnNextPayload,
	OnSelectChoicePayload,
	SubmitPayloadSchema,
	WizardFormRef,
} from "@CommonLib/src/frontend/WizardForm/types";
import { WizardFormField } from "@CommonLib/src/frontend/WizardForm/WizardFormField";
import { AiDisclaimerWrapper } from "@Src/components/AiDisclaimerWrapper";
import { FormRedirectionWrapper } from "@Src/components/FormRedirectionWrapper";
import { FormTimerWrapper } from "@Src/components/FormTimerWrapper";
import { GeolocationWrapper } from "@Src/components/GeolocationWrapper";
import { LinkedInWrapper } from "@Src/components/LinkedInWrapper";
import { NoSelectWrapper } from "@Src/components/NoSelectWrapper";
import { ShowIntro } from "@Src/components/ShowIntro";
import { TabVisibilityWrapper } from "@Src/components/TabVisibilityWrapper";
import { thanksResponsesPath } from "@Src/constants";
import {
	BrowserInfoProvider,
	useBrowserInfo,
} from "@Src/contexts/BrowserInfoContext";
import {
	GeolocationProvider,
	useGeolocation,
} from "@Src/contexts/GeolocationContext";
import { LinkedInProvider, useLinkedIn } from "@Src/contexts/LinkedInContext";
import { usePublicKey } from "@Src/contexts/PublicKeyContext";
import { sendEncryptedData } from "@Src/utils/encryption";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	Edit,
	useDataProvider,
	useNotify,
	useRecordContext,
} from "react-admin";
import { useNavigate } from "react-router-dom";

const GetRecordId = (props: { setRecordId: (recordId: string) => void }) => {
	const { setRecordId } = props;
	const record = useRecordContext();
	useEffect(() => {
		if (record?.id) {
			setRecordId(record.id.toString());
		}
	}, [record, setRecordId]);
	return null;
};

const GetDataBrowser = (props: {
	setBrowserInfo: (info: BrowserInfo) => void;
}) => {
	const { setBrowserInfo } = props;
	const { browserInfo } = useBrowserInfo();
	useEffect(() => {
		if (browserInfo) {
			setBrowserInfo(browserInfo);
		}
	}, [browserInfo, setBrowserInfo]);
	return null;
};

const GetDataGeolocation = (props: {
	setCoordinates: (coordinates: GeolocationCoordinates) => void;
}) => {
	const { setCoordinates } = props;
	const { coordinates } = useGeolocation();
	useEffect(() => {
		if (coordinates) {
			setCoordinates(coordinates);
		}
	}, [coordinates, setCoordinates]);
	return null;
};

const GetAuthLinkedInConfig = (props: {
	setAuthLinkedIn: (authLinkedIn: boolean) => void;
}) => {
	const { setAuthLinkedIn } = props;
	const record = useRecordContext();
	useEffect(() => {
		if (record?.["auth_linkedin"] !== undefined) {
			setAuthLinkedIn(record["auth_linkedin"]);
		}
	}, [record, setAuthLinkedIn]);
	return null;
};

const GetDataLinkedIn = (props: {
	setLinkedInEmail: (email: string | null) => void;
	setLinkedInName: (name: string | null) => void;
}) => {
	const { setLinkedInEmail, setLinkedInName } = props;
	const { user } = useLinkedIn();
	useEffect(() => {
		if (user && typeof user === "object") {
			const userObj = user as { email?: string; name?: string };
			setLinkedInEmail(userObj.email ?? null);
			setLinkedInName(userObj.name ?? null);
		}
	}, [user, setLinkedInEmail, setLinkedInName]);
	return null;
};

const GetMaxAllowedTime = (props: {
	setMaxAllowedTime: (maxAllowedTime: number) => void;
}) => {
	const { setMaxAllowedTime } = props;
	const record = useRecordContext();
	useEffect(() => {
		if (record?.["max_allowed_time"] !== undefined) {
			setMaxAllowedTime(record["max_allowed_time"]);
		}
	}, [record, setMaxAllowedTime]);
	return null;
};

// Used for 1st step of forms answering
export const ApplicationsPublicEdit = (props: ComponentPropI) => {
	const dataProvider = useDataProvider();
	const formRef = useRef<WizardFormRef>(null);
	const [recordId, setRecordId] = useState<string | null>(null);
	const [coordinates, setCoordinates] = useState<GeolocationCoordinates | null>(
		null,
	);
	const [browserInfo, setBrowserInfo] = useState<BrowserInfo | null>(null);
	const [authLinkedIn, setAuthLinkedIn] = useState<boolean>(false);
	const [linkedInEmail, setLinkedInEmail] = useState<string | null>(null);
	const [linkedInName, setLinkedInName] = useState<string | null>(null);
	const [maxAllowedTime, setMaxAllowedTime] = useState<number | null>(null);
	const formTimerExpiredRef = useRef<boolean>(false);
	const { publicKey } = usePublicKey();
	const notify = useNotify();
	const navigate = useNavigate();

	useEffect(() => {
		if (recordId) {
			Sentry.setUser({
				email: `application_public_${recordId}@nomachine.dev`,
			});
		}
		// necessary to send analytics now to get early browser info, IP address etc even if user stops
		// before starting the form
		// + also raise an error msg if that linkedin email already responded to this form
		if (recordId && publicKey) {
			const payload: AnalyticsPayloadSchema = {
				type: "flow_init",
				browser_info: browserInfo,
				linkedin_email: linkedInEmail,
			};
			sendEncryptedData(
				dataProvider,
				"analytics_public",
				recordId,
				payload,
				publicKey,
			);
		}
	}, [recordId, dataProvider, browserInfo, linkedInEmail, publicKey]);

	const onKeystroke = (payload: OnKeystrokePayload) => {
		if (!recordId || !publicKey) return;
		const analyticsPayload: AnalyticsPayloadSchema = {
			type: "keystroke",
			key: payload.key,
			field_key: payload.fieldKey,
			timestamp: payload.timestamp,
		};
		sendEncryptedData(
			dataProvider,
			"analytics_public",
			recordId,
			analyticsPayload,
			publicKey,
		);
	};

	const onNext = (payload: OnNextPayload) => {
		if (!recordId || !publicKey) return;
		const analyticsPayload: AnalyticsPayloadSchema = {
			type: "form_next",
			from_step_id: payload.fromStepId,
			from_field_id: payload.fromFieldId,
		};
		sendEncryptedData(
			dataProvider,
			"analytics_public",
			recordId,
			analyticsPayload,
			publicKey,
		);
	};

	const onFileUpload = (payload: OnFileUploadPayload) => {
		if (!recordId || !publicKey) return;
		const analyticsPayload: AnalyticsPayloadSchema = {
			type: "file_upload",
			field_key: payload.fieldKey,
			timestamp: payload.timestamp,
		};
		sendEncryptedData(
			dataProvider,
			"analytics_public",
			recordId,
			analyticsPayload,
			publicKey,
		);
	};

	const onSelectChoice = (payload: OnSelectChoicePayload) => {
		if (!recordId || !publicKey) return;
		const analyticsPayload: AnalyticsPayloadSchema = {
			type: "select_choice",
			field_key: payload.fieldKey,
			timestamp: payload.timestamp,
		};
		sendEncryptedData(
			dataProvider,
			"analytics_public",
			recordId,
			analyticsPayload,
			publicKey,
		);
	};

	const onFormStart = async () => {
		if (!recordId || !publicKey) {
			throw Error("Should never happen");
		}
		// necessary to call endpoint here to signal that user started the form
		const payload: AnalyticsPayloadSchema = {
			type: "form_start",
			form_started: true,
		};
		await sendEncryptedData(
			dataProvider,
			"analytics_public",
			recordId,
			payload,
			publicKey,
		);
	};

	const onLeftTab = useCallback(() => {
		if (!recordId || !publicKey) return;
		const analyticsPayload: AnalyticsPayloadSchema = {
			type: "left_tab",
		};
		sendEncryptedData(
			dataProvider,
			"analytics_public",
			recordId,
			analyticsPayload,
			publicKey,
		);
	}, [recordId, dataProvider, publicKey]);

	const onFormTimerExpired = async () => {
		formTimerExpiredRef.current = true;
		await formRef.current?.submitForm();
	};

	const sendData = async (payload: SubmitPayloadSchema) => {
		// we define full payload - form part, but also contextual part
		const fullPayload: FullPayloadSchema = {
			payload,
			extraPayload: {
				has_form_timer_expired: formTimerExpiredRef.current,
				coordinates,
				linkedin_email: linkedInEmail,
				linkedin_name: linkedInName,
			},
		};

		try {
			if (!recordId || !publicKey) throw Error();
			const resourceName = "applications_public";
			await sendEncryptedData(
				dataProvider,
				resourceName,
				recordId,
				fullPayload,
				publicKey,
			);
			notify("Submitted successfully", { type: "info" });
			navigate(`${basePath}${thanksResponsesPath}`);
		} catch (error: unknown) {
			console.log(error);
			notify("Error during submit", {
				type: "error",
			});
		}
	};

	return (
		<Edit disableAuthentication={props.isPublic} mutationMode="pessimistic">
			<GetRecordId setRecordId={setRecordId} />
			<GetMaxAllowedTime setMaxAllowedTime={setMaxAllowedTime} />
			{/* For LinkedIn auth flow context */}
			<LinkedInProvider>
				<GetAuthLinkedInConfig setAuthLinkedIn={setAuthLinkedIn} />
				<GetDataLinkedIn
					setLinkedInEmail={setLinkedInEmail}
					setLinkedInName={setLinkedInName}
				/>
				{/* For LinkedIn auth flow if form has linkedin auth enabled */}
				<LinkedInWrapper
					isLinkedInEnabled={authLinkedIn}
					dataProvider={dataProvider}
					recordId={recordId}
					publicKey={publicKey}
				>
					{/* Redirect if form already submitted */}
					<FormRedirectionWrapper>
						{/* Gets basic browser info */}
						<BrowserInfoProvider>
							<GetDataBrowser setBrowserInfo={setBrowserInfo} />
							{/* Context to store geolocation info from browser */}
							<GeolocationProvider>
								<GetDataGeolocation setCoordinates={setCoordinates} />
								{/* Flow to show Intro */}
								<ShowIntro
									dataProvider={dataProvider}
									recordId={recordId}
									publicKey={publicKey}
								>
									{/* Flow to show AI disclaimer */}
									<AiDisclaimerWrapper
										dataProvider={dataProvider}
										recordId={recordId}
										publicKey={publicKey}
										disclaimers={[
											{
												label:
													"I confirm that I will not use any AI tool when answering this form.",
											},
											{
												label:
													"I confirm that I will not leave this browser tab until I have finished answering and submitted the form.",
											},
										]}
									>
										{/* Flow to request browser geolocation permission */}
										<GeolocationWrapper
											dataProvider={dataProvider}
											recordId={recordId}
											publicKey={publicKey}
										>
											{/* Tab visibility detection */}
											<TabVisibilityWrapper onLeftTab={onLeftTab}>
												{/* Form Timer system */}
												<FormTimerWrapper
													maxAllowedTimeMinutes={maxAllowedTime}
													onTimerExpired={onFormTimerExpired}
												>
													{/* Prevent text selection */}
													<NoSelectWrapper>
														<WizardFormField
															sendData={sendData}
															formRef={formRef}
															onMount={onFormStart}
															onKeystroke={onKeystroke}
															onNext={onNext}
															onFileUpload={onFileUpload}
															onSelectChoice={onSelectChoice}
														/>
													</NoSelectWrapper>
												</FormTimerWrapper>
											</TabVisibilityWrapper>
										</GeolocationWrapper>
									</AiDisclaimerWrapper>
								</ShowIntro>
							</GeolocationProvider>
						</BrowserInfoProvider>
					</FormRedirectionWrapper>
				</LinkedInWrapper>
			</LinkedInProvider>
		</Edit>
	);
};
